import React, { useState, useCallback, useEffect } from "react";
import { useTheme, IconButton, TextField } from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import DownloadIcon from "@mui/icons-material/Download";
import ContentCopyIcon from "@mui/icons-material/ContentCopy";
import DSCircularProgress from "../../system-design/DSCircularProgress";
import DSIconButton from "../../system-design/DSIconButton";
import DSCard from "../../system-design/DSCard";
import DSCardContent from "../../system-design/DSCardContent";
import DSChip from "../../system-design/DSChip";
import DSBox from "../../system-design/DSBox";
import DSTextField from "../../system-design/DSTextField";
import DSLabelTypography from "../../system-design/DSLabelTypography";
import DSTypography from "../../system-design/DSTypography";
import DSPrimaryButton from "../../system-design/DSPrimaryButton";
import DSButton from "../../system-design/DSButton";
import KlydoTable from "../../../components/Widgets/KlydoTable";
import KlydosPickDialog from "../../Gifts/GiftCategories/components/KlydosPickDialog";
import UserDropdown from "./UserDropdown";
import { Klydo, hasSound } from "../../../api/klydo";
import KlydoPickModalForVideo from "./KlydoPickModalForVideo";
import { User } from "../../../api/admin";
import CreatorCard from "./CreatorCard";
import DSSwitch from "../../system-design/DSSwitch";
import DSFormControlLabel from "../../system-design/DSFormControlLabel";
import useGeneratePreviewVideoMutation from "../../../api/hooks/useGeneratePreviewVideoMutation";
import useUploadCollectionCoverMutation from "../hooks/useUploadCollectionCoverMutation";
import useUploadCollectionLogoMutation from "../hooks/useUploadCollectionLogoMutation";
import useUploadCollectionLogotypeMutation from "../hooks/useUploadCollectionLogotypeMutation";
import useUploadCollectionThumbnailBackgroundMutation from "../hooks/useUploadCollectionThumbnailBackgroundMutation";
import useUploadCollectionSplashBackgroundMutation from "../hooks/useUploadCollectionSplashBackgroundMutation";
import useUploadCollectionMenuLogoMutation from "../hooks/useUploadCollectionMenuLogoMutation";
import StringChipEditor from "../../../components/Widgets/StringChipEditor";
import { ProductPageMetadata } from "../../../api/collection";
import CollectionAssetFileInput from "./CollectionAssetFileInput/CollectionAssetFileInput";
import ColorPickerInput from "./ColorPickerInput";

export const COLLECTION_DESCRIPTION_LIMIT = 400;
export const COLLECTION_NAME_LIMIT = 20;

export type CollectionFormProps = {
  collectionId: string;
  name: string;
  setName: (name: string) => void;
  description: string;
  klydosIds: string[];
  setKlydosIds: React.Dispatch<React.SetStateAction<string[]>>;
  setDescription: (description: string) => void;
  cover: string;
  setCover: (cover: string) => void;
  logo: string;
  setLogo: (logo: string) => void;
  logotype: string;
  setLogotype: (logotype: string) => void;
  thumbnailBackground: string;
  setThumbnailBackground: (thumbnailBackground: string) => void;
  splashBackground: string;
  setSplashBackground: (splashBackground: string) => void;
  menuLogo: string;
  setMenuLogo: (menuLogo: string) => void;
  badgeColorHex: string;
  setBadgeColorHex: (badgeColorHex: string) => void;
  holdPendulumLogo: boolean;
  setHoldPendulumLogo: (value: boolean) => void;
  onUserSelect: (value: string) => void;
  creatorId: string;
  creator: User | undefined;
  klydos: Klydo[];
  labels: string[];
  setLabels: (labels: string[]) => void;
  productPageMetadata?: ProductPageMetadata;
  disabled?: boolean;
  // When true, the text fields (name, description, tags) stay editable even
  // while the rest of the form is `disabled`. Used to edit an approved
  // collection's copy without touching its assets/klydos.
  textEditable?: boolean;
  // Same idea for the cover uploader alone: marketing replaces the main image
  // of an already-published collection without unlocking the other assets.
  coverEditable?: boolean;
  // Reports the Cloudinary cover upload state to the parent, so a "save" action
  // outside this form can be blocked until the new url has actually arrived.
  onCoverUploadingChange?: (isUploading: boolean) => void;
};

const CollectionForm = ({
  collectionId,
  name,
  setName,
  description,
  setDescription,
  cover,
  klydosIds,
  setKlydosIds,
  setCover,
  logo,
  setLogo,
  logotype,
  setLogotype,
  thumbnailBackground,
  setThumbnailBackground,
  splashBackground,
  setSplashBackground,
  menuLogo,
  setMenuLogo,
  badgeColorHex,
  setBadgeColorHex,
  holdPendulumLogo,
  setHoldPendulumLogo,
  onUserSelect,
  creatorId,
  creator,
  klydos,
  labels,
  setLabels,
  disabled = false,
  textEditable = false,
  coverEditable = false,
  onCoverUploadingChange,
}: CollectionFormProps) => {
  const { spacing, palette } = useTheme();

  // Text fields are editable when the form is fully enabled, or when only the
  // text fields have been unlocked for an approved-collection edit.
  const textDisabled = disabled && !textEditable;
  const coverDisabled = disabled && !coverEditable;

  const hasChimes = klydos.some(
    (klydo) => klydo.chimesUrls && klydo.chimesUrls.length > 0,
  );
  const hasKlydoSound = klydos.some((klydo) =>
    hasSound(klydo.capabilitiesV2?.animationSound?.mode),
  );

  const [addKlydosDialogOpen, setAddKlydosDialogOpen] = useState(false);
  const [videoModalOpen, setVideoModalOpen] = useState(false);
  const [coverFileName, setCoverFileName] = useState("");
  const [logoFileName, setLogoFileName] = useState("");
  const [logotypeFileName, setLogotypeFileName] = useState("");
  const [thumbnailBackgroundFileName, setThumbnailBackgroundFileName] =
    useState("");
  const [splashBackgroundFileName, setSplashBackgroundFileName] = useState("");
  const [menuLogoFileName, setMenuLogoFileName] = useState("");

  const {
    mutate: generatePreviewVideo,
    isPending: isGenerating,
    error: videoError,
    isSuccess: videoSuccess,
    data: videoData,
  } = useGeneratePreviewVideoMutation();
  const [generatedVideoUrl, setGeneratedVideoUrl] = useState<string>("");
  const [videoCopied, setVideoCopied] = useState(false);

  // Update video URL when generation succeeds
  useEffect(() => {
    if (videoSuccess && videoData?.videoUrl) {
      setGeneratedVideoUrl(videoData.videoUrl);
    }
  }, [videoSuccess, videoData]);

  const handleCopyVideoUrl = async () => {
    if (generatedVideoUrl) {
      try {
        await navigator.clipboard.writeText(generatedVideoUrl);
        setVideoCopied(true);
        setTimeout(() => setVideoCopied(false), 3000);
      } catch (error) {
        console.error("Failed to copy video URL:", error);
      }
    }
  };

  // Upload hooks
  const { mutate: uploadCollectionCover, isPending: isUploadingCover } =
    useUploadCollectionCoverMutation({
      onError: () => {
        console.error("Failed to upload collection cover");
      },
    });

  // Without this the dialog's "Save Details" stays enabled while the cover is
  // still uploading: clicking it sends the OLD url (or none at all) and reports
  // "Saved" although the new cover was never applied.
  useEffect(() => {
    onCoverUploadingChange?.(isUploadingCover);
  }, [isUploadingCover, onCoverUploadingChange]);

  const { mutate: uploadCollectionLogo, isPending: isUploadingLogo } =
    useUploadCollectionLogoMutation({
      onError: () => {
        console.error("Failed to upload collection logo");
      },
    });

  const { mutate: uploadCollectionLogotype, isPending: isUploadingLogotype } =
    useUploadCollectionLogotypeMutation({
      onError: () => {
        console.error("Failed to upload collection logotype");
      },
    });

  const {
    mutate: uploadCollectionThumbnailBackground,
    isPending: isUploadingThumbnailBackground,
  } = useUploadCollectionThumbnailBackgroundMutation({
    onError: () => {
      console.error("Failed to upload collection thumbnail background");
    },
  });

  const {
    mutate: uploadCollectionSplashBackground,
    isPending: isUploadingSplashBackground,
  } = useUploadCollectionSplashBackgroundMutation({
    onError: () => {
      console.error("Failed to upload collection splash background");
    },
  });

  const { mutate: uploadCollectionMenuLogo, isPending: isUploadingMenuLogo } =
    useUploadCollectionMenuLogoMutation({
      onError: () => {
        console.error("Failed to upload collection menu logo");
      },
    });

  const handleCoverChange = useCallback(
    (event: React.ChangeEvent<HTMLInputElement>) => {
      const file = event.target.files?.[0];
      if (!file) return;

      setCoverFileName(file.name);
      const reader = new FileReader();
      reader.onloadend = () => {
        const uploadInput = {
          file: reader.result as string,
        };

        uploadCollectionCover(uploadInput, {
          onSuccess: (response: { url: string }) => {
            setCover(response.url);
          },
        });
      };

      reader.readAsDataURL(file);
    },
    [uploadCollectionCover, setCover],
  );

  const handleLogoChange = useCallback(
    (event: React.ChangeEvent<HTMLInputElement>) => {
      const file = event.target.files?.[0];
      if (!file) return;

      setLogoFileName(file.name);
      const reader = new FileReader();
      reader.onloadend = () => {
        const uploadInput = {
          file: reader.result as string,
        };

        uploadCollectionLogo(uploadInput, {
          onSuccess: (response: { url: string }) => {
            setLogo(response.url);
          },
        });
      };

      reader.readAsDataURL(file);
    },
    [uploadCollectionLogo, setLogo],
  );

  const handleLogotypeChange = useCallback(
    (event: React.ChangeEvent<HTMLInputElement>) => {
      const file = event.target.files?.[0];
      if (!file) return;

      setLogotypeFileName(file.name);
      const reader = new FileReader();
      reader.onloadend = () => {
        const uploadInput = {
          file: reader.result as string,
        };

        uploadCollectionLogotype(uploadInput, {
          onSuccess: (response: { url: string }) => {
            setLogotype(response.url);
          },
        });
      };

      reader.readAsDataURL(file);
    },
    [uploadCollectionLogotype, setLogotype],
  );

  const handleThumbnailBackgroundChange = useCallback(
    (event: React.ChangeEvent<HTMLInputElement>) => {
      const file = event.target.files?.[0];
      if (!file) return;

      setThumbnailBackgroundFileName(file.name);
      const reader = new FileReader();
      reader.onloadend = () => {
        const uploadInput = {
          file: reader.result as string,
        };

        uploadCollectionThumbnailBackground(uploadInput, {
          onSuccess: (response: { url: string }) => {
            setThumbnailBackground(response.url);
          },
        });
      };

      reader.readAsDataURL(file);
    },
    [uploadCollectionThumbnailBackground, setThumbnailBackground],
  );

  const handleSplashBackgroundChange = useCallback(
    (event: React.ChangeEvent<HTMLInputElement>) => {
      const file = event.target.files?.[0];
      if (!file) return;

      setSplashBackgroundFileName(file.name);
      const reader = new FileReader();
      reader.onloadend = () => {
        uploadCollectionSplashBackground(
          { file: reader.result as string },
          {
            onSuccess: (response: { url: string }) => {
              setSplashBackground(response.url);
            },
          },
        );
      };

      reader.readAsDataURL(file);
    },
    [uploadCollectionSplashBackground, setSplashBackground],
  );

  const handleMenuLogoChange = useCallback(
    (event: React.ChangeEvent<HTMLInputElement>) => {
      const file = event.target.files?.[0];
      if (!file) return;

      setMenuLogoFileName(file.name);
      const reader = new FileReader();
      reader.onloadend = () => {
        uploadCollectionMenuLogo(
          { file: reader.result as string },
          {
            onSuccess: (response: { url: string }) => {
              setMenuLogo(response.url);
            },
          },
        );
      };

      reader.readAsDataURL(file);
    },
    [uploadCollectionMenuLogo, setMenuLogo],
  );

  const handleGenerateVideoWithSelectedKlydos = (
    selectedKlydos: Klydo[],
    displayDuration?: number,
    attachToProduct?: boolean,
  ) => {
    generatePreviewVideo({
      klydoIds: selectedKlydos.map((k) => k.id),
      // Attaching resolves the Shopify product from this id, so it has to be
      // the bare collection id. A preview keeps the timestamp so repeated
      // runs do not overwrite the collection's stored video.
      itemId: attachToProduct ? collectionId : `${collectionId}-${Date.now()}`,
      pendulumLogo: logo,
      displayDuration: displayDuration || 5000,
      attachToProduct,
    });
  };

  return (
    <>
      <DSBox
        sx={{
          padding: spacing(3),
          display: "flex",
          flexDirection: "column",
          gap: spacing(3),
        }}
      >
        <DSCard
          sx={{
            padding: spacing(3),
            borderRadius: 2,
            boxShadow: "0 2px 8px rgba(0,0,0,0.1)",
            border: "1px solid",
            borderColor: palette.divider,
            mb: spacing(3),
          }}
        >
          <DSCardContent sx={{ padding: 0 }}>
            <DSTypography
              variant="subtitle2"
              sx={{
                mb: 2,
                fontWeight: 600,
                color: palette.text.secondary,
                textTransform: "uppercase",
                fontSize: "0.75rem",
                letterSpacing: "0.5px",
              }}
            >
              Visual Assets
            </DSTypography>
            <DSBox
              sx={{
                display: "grid",
                gridTemplateColumns: "repeat(4, 1fr)",
                gap: 2,
                mb: 3,
              }}
            >
              <CollectionAssetFileInput
                label="Cover"
                fileSpecsText="1080 x 1080 px\nJPG, PNG"
                acceptedFormats=".png, .jpeg, .jpg"
                secureUrl={cover}
                onFileChange={handleCoverChange}
                onRemoveFile={() => {
                  setCover("");
                  setCoverFileName("");
                }}
                fileName={coverFileName}
                isLoading={isUploadingCover}
                required
                disabled={coverDisabled}
              />

              <CollectionAssetFileInput
                label="Logotype"
                fileSpecsText="1080 x 1080 px\nJPG, PNG"
                acceptedFormats=".png, .jpeg, .jpg"
                secureUrl={logotype}
                onFileChange={handleLogotypeChange}
                onRemoveFile={() => {
                  setLogotype("");
                  setLogotypeFileName("");
                }}
                fileName={logotypeFileName}
                isLoading={isUploadingLogotype}
                required
                disabled={disabled}
              />

              {/* Logo Symbol */}
              <DSBox>
                <CollectionAssetFileInput
                  label="Logo symbol"
                  fileSpecsText="1080 x 1080 px\nJPG, PNG"
                  acceptedFormats=".png, .jpeg, .jpg"
                  secureUrl={logo}
                  onFileChange={handleLogoChange}
                  onRemoveFile={() => {
                    setLogo("");
                    setLogoFileName("");
                  }}
                  fileName={logoFileName}
                  isLoading={isUploadingLogo}
                  required
                  disabled={disabled}
                />
                <DSFormControlLabel
                  control={
                    <DSSwitch
                      checked={holdPendulumLogo}
                      onChange={(e) => setHoldPendulumLogo(e.target.checked)}
                      color="primary"
                      disabled={disabled}
                    />
                  }
                  label="Hold Pendulum Logo"
                  sx={{
                    mt: spacing(1),
                    "& .MuiFormControlLabel-label": {
                      fontSize: "12px",
                      color: palette.text.secondary,
                    },
                  }}
                  disabled={disabled}
                />
              </DSBox>

              <DSBox>
                <CollectionAssetFileInput
                  label="Thumbnail background"
                  fileSpecsText="1080 x 1080 px\nJPG, PNG"
                  acceptedFormats=".png, .jpeg, .jpg"
                  secureUrl={thumbnailBackground}
                  onFileChange={handleThumbnailBackgroundChange}
                  onRemoveFile={() => {
                    setThumbnailBackground("");
                    setThumbnailBackgroundFileName("");
                  }}
                  fileName={thumbnailBackgroundFileName}
                  isLoading={isUploadingThumbnailBackground}
                  required
                  disabled={disabled}
                />
                <ColorPickerInput
                  color={badgeColorHex || "#000000"}
                  onChange={setBadgeColorHex}
                  disabled={disabled}
                />
              </DSBox>
            </DSBox>

            <DSBox
              sx={{
                display: "flex",
                flexDirection: "column",
                gap: spacing(2),
              }}
            >
              <DSBox
                sx={{
                  display: "flex",
                  flexDirection: "column",
                  gap: spacing(1),
                }}
              >
                <DSLabelTypography>Name Your Collection</DSLabelTypography>
                <DSTextField
                  value={name}
                  placeholder="My Klydo Collection"
                  onChange={(e) => setName(e.target.value)}
                  fullWidth
                  disabled={textDisabled}
                  slotProps={{
                    input: {
                      inputProps: { maxLength: COLLECTION_NAME_LIMIT },
                    },
                  }}
                />
              </DSBox>
              <DSBox
                sx={{
                  display: "flex",
                  flexDirection: "column",
                  gap: spacing(1),
                }}
              >
                <DSLabelTypography>Collection Description</DSLabelTypography>
                <DSTextField
                  value={description}
                  onChange={(e) => setDescription(e.target.value)}
                  multiline
                  rows={6}
                  fullWidth
                  disabled={textDisabled}
                  placeholder="My Collection is special since..."
                  slotProps={{
                    input: {
                      inputProps: { maxLength: COLLECTION_DESCRIPTION_LIMIT },
                    },
                  }}
                />
              </DSBox>
            </DSBox>
          </DSCardContent>
        </DSCard>

        {/* Themed-collection assets. These stay editable even when the rest of
            the form is disabled (an admin opens this modal from the themed
            picker specifically to add them), and are required for themed
            collections. Each offers a shortcut to reuse an existing asset. */}
        <DSCard
          sx={{
            padding: spacing(3),
            borderRadius: 2,
            boxShadow: "0 2px 8px rgba(0,0,0,0.1)",
            border: "1px solid",
            borderColor: palette.divider,
            mb: spacing(3),
          }}
        >
          <DSCardContent sx={{ padding: 0 }}>
            <DSTypography
              variant="subtitle2"
              sx={{
                mb: 2,
                fontWeight: 600,
                color: palette.text.secondary,
                textTransform: "uppercase",
                fontSize: "0.75rem",
                letterSpacing: "0.5px",
              }}
            >
              Themed Collection Assets
            </DSTypography>
            <DSBox
              sx={{
                display: "grid",
                gridTemplateColumns: "repeat(2, 1fr)",
                gap: 2,
              }}
            >
              <DSBox>
                <CollectionAssetFileInput
                  label="Splash background"
                  fileSpecsText="1080 x 1080 px\nJPG, PNG"
                  acceptedFormats=".png, .jpeg, .jpg"
                  helpTooltipContent="Fills the whole inner circle of the themed collection's menu page."
                  secureUrl={splashBackground}
                  onFileChange={handleSplashBackgroundChange}
                  onRemoveFile={() => {
                    setSplashBackground("");
                    setSplashBackgroundFileName("");
                  }}
                  fileName={splashBackgroundFileName}
                  isLoading={isUploadingSplashBackground}
                  required
                />
                <DSButton
                  size="small"
                  onClick={() => setSplashBackground(cover)}
                  disabled={!cover || isUploadingSplashBackground}
                  sx={{ mt: spacing(1), textTransform: "none" }}
                >
                  Use cover
                </DSButton>
              </DSBox>

              <DSBox>
                <CollectionAssetFileInput
                  label="Menu logo"
                  fileSpecsText="1080 x 1080 px\nJPG, PNG"
                  acceptedFormats=".png, .jpeg, .jpg"
                  helpTooltipContent="The themed collection's logo in the main menu."
                  secureUrl={menuLogo}
                  onFileChange={handleMenuLogoChange}
                  onRemoveFile={() => {
                    setMenuLogo("");
                    setMenuLogoFileName("");
                  }}
                  fileName={menuLogoFileName}
                  isLoading={isUploadingMenuLogo}
                  required
                />
                <DSButton
                  size="small"
                  onClick={() => setMenuLogo(logo)}
                  disabled={!logo || isUploadingMenuLogo}
                  sx={{ mt: spacing(1), textTransform: "none" }}
                >
                  Use pendulum logo
                </DSButton>
              </DSBox>
            </DSBox>
          </DSCardContent>
        </DSCard>

        <DSBox
          sx={{
            display: "flex",
            flexDirection: "column",
            gap: spacing(2),
          }}
        >
          <DSBox
            sx={{
              display: "flex",
              flexDirection: "column",
              gap: spacing(1),
            }}
          >
            <StringChipEditor
              label="Tags"
              values={labels}
              onChange={setLabels}
              placeholder="Add a tag (e.g., Featured, New, Popular)"
              helperText="Add tags to categorize this collection on the product page"
              disabled={textDisabled}
            />
          </DSBox>
          {(hasChimes || hasKlydoSound) && (
            <DSBox
              sx={{
                display: "flex",
                flexDirection: "column",
                gap: spacing(1),
              }}
            >
              <DSLabelTypography>Special Attributes</DSLabelTypography>
              <DSBox sx={{ display: "flex", gap: 1, flexWrap: "wrap" }}>
                {hasChimes && (
                  <DSChip label="Chimes" size="small" color="success" />
                )}
                {hasKlydoSound && (
                  <DSChip label="Sound" size="small" color="success" />
                )}
              </DSBox>
            </DSBox>
          )}
          <DSBox
            sx={{
              display: "flex",
              flexDirection: "row",
              justifyContent: "space-between",
              width: "100%",
              gap: spacing(4),
            }}
          >
            <DSBox
              sx={{
                display: "flex",
                flexDirection: "column",
                gap: spacing(1),
                width: "100%",
              }}
            >
              <DSLabelTypography>Collection ID</DSLabelTypography>
              <DSTextField
                disabled
                fullWidth
                value={collectionId}
                slotProps={{
                  input: {
                    style: { cursor: "text" },
                  },
                }}
              />
            </DSBox>
          </DSBox>
          <DSBox
            sx={{
              display: "flex",
              flexDirection: "row",
              justifyContent: "space-between",
              width: "100%",
              gap: spacing(4),
            }}
          >
            <DSBox
              sx={{
                display: "flex",
                flexDirection: "column",
                gap: spacing(1),
                width: "100%",
              }}
            >
              <DSLabelTypography>Location</DSLabelTypography>
              <DSTextField disabled fullWidth placeholder="Global" />
            </DSBox>
            <DSBox
              sx={{
                display: "flex",
                flexDirection: "column",
                gap: spacing(1),
                width: "100%",
              }}
            >
              <DSLabelTypography>Duration</DSLabelTypography>
              <DSTextField disabled fullWidth placeholder="Always" />
            </DSBox>
            <DSBox
              sx={{
                display: "flex",
                flexDirection: "column",
                gap: spacing(1),
                width: "100%",
              }}
            >
              <DSLabelTypography>Visibility</DSLabelTypography>
              <DSTextField disabled fullWidth placeholder="Public" />
            </DSBox>
          </DSBox>
        </DSBox>

        <DSCard
          sx={{
            padding: spacing(3),
            borderRadius: 2,
            boxShadow: "0 2px 8px rgba(0,0,0,0.1)",
            border: "1px solid",
            borderColor: palette.divider,
          }}
        >
          <DSCardContent sx={{ padding: 0 }}>
            <DSBox
              sx={{
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
                marginBottom: spacing(2),
              }}
            >
              <DSBox sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                <DSTypography variant="h6" sx={{ fontWeight: 600 }}>
                  Klydos Collection
                </DSTypography>
                <DSChip
                  label={klydosIds?.length || 0}
                  size="small"
                  color="primary"
                  sx={{ fontWeight: 600 }}
                />
              </DSBox>
              <DSBox sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                <DSIconButton
                  onClick={() => setVideoModalOpen(true)}
                  disabled={isGenerating || klydos.length === 0}
                  sx={{
                    color: palette.primary.main,
                    "&:hover": {
                      transform: "scale(1.1)",
                      backgroundColor: palette.primary.main + "10",
                    },
                    "&:disabled": {
                      color: palette.action.disabled,
                    },
                  }}
                >
                  {isGenerating ? (
                    <DSCircularProgress size={20} />
                  ) : (
                    <DownloadIcon sx={{ fontSize: 20 }} />
                  )}
                </DSIconButton>
                {videoError && (
                  <DSTypography
                    sx={{
                      color: palette.error.main,
                      fontSize: "12px",
                      maxWidth: "200px",
                    }}
                  >
                    {videoError.message || "Failed to generate video"}
                  </DSTypography>
                )}
                {generatedVideoUrl && (
                  <DSBox
                    sx={{
                      display: "flex",
                      flexDirection: "column",
                      gap: 0.5,
                      backgroundColor: "surface.panel",
                      padding: "8px 12px",
                      borderRadius: "4px",
                      boxShadow: "0 2px 8px rgba(0,0,0,0.2)",
                    }}
                  >
                    <DSBox
                      sx={{
                        display: "flex",
                        alignItems: "center",
                        gap: 1,
                      }}
                    >
                      <TextField
                        value={generatedVideoUrl}
                        size="small"
                        InputProps={{
                          readOnly: true,
                        }}
                        sx={{
                          width: "300px",
                          "& .MuiInputBase-root": {
                            fontSize: "11px",
                          },
                          "& .MuiInputBase-input": {
                            padding: "4px 8px",
                          },
                        }}
                      />
                      <IconButton
                        size="small"
                        onClick={handleCopyVideoUrl}
                        title="Copy video URL"
                        sx={{
                          color: videoCopied ? "success.main" : "primary.main",
                        }}
                      >
                        <ContentCopyIcon sx={{ fontSize: 18 }} />
                      </IconButton>
                      {videoCopied && (
                        <DSTypography
                          sx={{
                            color: "success.main",
                            fontSize: "11px",
                            fontWeight: 600,
                          }}
                        >
                          Copied!
                        </DSTypography>
                      )}
                    </DSBox>
                    <DSTypography
                      sx={{
                        color: "text.secondary",
                        fontSize: "10px",
                        fontStyle: "italic",
                      }}
                    >
                      Note: URL will be valid after 5-15 minutes
                    </DSTypography>
                  </DSBox>
                )}
              </DSBox>
            </DSBox>
            <DSBox
              sx={{
                width: "100%",
                borderRadius: 1,
                border: "1px solid " + palette.divider,
                height: "500px",
                marginBottom: spacing(2),
                position: "relative",
              }}
            >
              <KlydoTable
                klydos={klydos}
                canRemove={!disabled}
                showHeaders={true}
                onRemove={(klydoId) =>
                  setKlydosIds((prev) => prev.filter((id) => id !== klydoId))
                }
              />
            </DSBox>
            <DSPrimaryButton
              onClick={() => setAddKlydosDialogOpen(true)}
              startIcon={<AddIcon />}
              disabled={disabled}
              sx={{
                borderRadius: 2,
                textTransform: "none",
                fontWeight: 600,
                padding: spacing(1.5, 3),
                boxShadow: "0 2px 4px rgba(0,0,0,0.1)",
                "&:hover": {
                  boxShadow: "0 4px 8px rgba(0,0,0,0.15)",
                },
              }}
            >
              Add Klydos
            </DSPrimaryButton>
          </DSCardContent>
        </DSCard>
        <DSBox
          sx={{
            display: "flex",
            flexDirection: "column",
            width: "100%",
            gap: spacing(4),
            alignItems: "flex-start",
          }}
        >
          <DSBox>
            <DSLabelTypography
              sx={{ marginBottom: spacing(1), fontWeight: 600 }}
            >
              Creator
            </DSLabelTypography>
            <UserDropdown id={creatorId} onOptionSelect={onUserSelect} />
          </DSBox>

          <CreatorCard creator={creator} />
        </DSBox>
      </DSBox>
      {addKlydosDialogOpen && (
        <KlydosPickDialog
          selectedKlydos={klydosIds}
          onClose={() => setAddKlydosDialogOpen(false)}
          onPickKlydos={(klydos) => {
            setKlydosIds(klydos);
            setAddKlydosDialogOpen(false);
          }}
        />
      )}
      {videoModalOpen && (
        <KlydoPickModalForVideo
          collectionKlydos={klydos}
          onClose={() => setVideoModalOpen(false)}
          onGenerateVideo={handleGenerateVideoWithSelectedKlydos}
        />
      )}
    </>
  );
};

export default CollectionForm;
